import { readdirSync, readFileSync, statSync } from 'fs';
import { join, relative, extname, basename } from 'path';

// =============================================================================
// Types
// =============================================================================

export interface ExploreOptions {
  path: string;
  depth: number;
  extensions: string[];
  excludeDirs: string[];
  markersOnly: boolean;
}

export interface DiscoveredPrompt {
  file: string;
  line: number;
  preview: string;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  /**
   * File-level template variable delimiter inferred by
   * `inferPromptVariables()` over the whole file content (not per-line).
   * `'single'` = `{foo}` (platform canonical / LangChain PromptTemplate),
   * `'double'` = `{{foo}}` (Handlebars / Mustache / Liquid / Jinja2).
   * Every prompt hit discovered in the same file shares the same delimiter —
   * the inference is majority-wins per FILE, tie-break to `single`.
   * Surfaced via `mutagent explore --json` so coding agents know how to
   * enumerate variables when uploading the prompt. See
   * `concepts/prompt-variables.md` in the SKILL for the full contract.
   */
  delimiter: TemplateDelimiter;
}

export interface DiscoveredDataset {
  file: string;
  name: string;
  items: number;
}

export interface MarkerEntry {
  file: string;
  line: number;
  type: 'prompt' | 'dataset' | 'evaluation';
  platformId?: string;
}

export interface DiscoveredAgent {
  file: string;
  line: number;
  preview: string;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface ExploreResult {
  prompts: DiscoveredPrompt[];
  datasets: DiscoveredDataset[];
  markers: MarkerEntry[];
  agents: DiscoveredAgent[];
}

// =============================================================================
// Heuristic Patterns
// =============================================================================

// Template variable patterns — see inferPromptVariables() below for the
// delimiter-aware entry point. These raw regexes are kept here as the
// tokenizers the inference function composes.
//
// DOUBLE-brace: {{foo}} (Handlebars / Mustache / LiquidJS / Jinja2)
const DOUBLE_VAR_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
// SINGLE-brace: {foo} (LangChain PromptTemplate, MutagenT platform canonical)
// We exclude two shapes to avoid JSON false-positives:
//   1. `{{` (part of a double-brace)  → handled by negative lookbehind (?<!\{)
//   2. `{"..."`  (JSON key)           → handled by negative lookahead for `"`
// The (?<!\{) + (?!\{) combo prevents overlap with DOUBLE_VAR_PATTERN.
const SINGLE_VAR_PATTERN = /(?<![\\{])\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}(?!\})/g;
// Fenced markdown code block — stripped before counting so that
// prompt documentation embedding ``` {"key": "val"} ``` does not leak.
const FENCED_CODE_PATTERN = /```[\s\S]*?```/g;

// Prompt-like constant names
const PROMPT_NAME_PATTERN = /(?:const|let|var|export)\s+\w*(?:prompt|system|agent|instruction|template)\w*\s*=/i;

// JSON schema indicators
const SCHEMA_PATTERN = /["']?(?:inputSchema|outputSchema|properties|required)["']?\s*[=:]/;

// Only match z.object() when variable name suggests prompt/output/input
const ZOD_PROMPT_SCHEMA_PATTERN = /(?:const|let|var|export)\s+\w*(?:prompt|output|input|response|schema|message)\w*\s*=\s*z\.object\s*\(/i;

// Pydantic model definitions
const PYDANTIC_PATTERN = /class\s+\w+\s*\(\s*(?:BaseModel|BaseSettings)\s*\)/;

// MutagenT marker pattern
const MARKER_START_PATTERN = /MutagenT:START\s+(\w+)(?:\s+id=(\S+))?/;

// =============================================================================
// Agent Heuristic Patterns (multi-turn / tool-calling indicators)
// =============================================================================

// Known AI/agent framework import patterns — files importing these get high confidence.
// Uses prefix matching for packages that have sub-paths (e.g. @langchain/core/prompts).
const AI_FRAMEWORK_IMPORT_PATTERNS: { pattern: RegExp; reason: string }[] = [
  { pattern: /from\s+['"]@langchain\/core(?:\/[^'"]*)?['"]/, reason: 'langchain-core-import' },
  { pattern: /from\s+['"]langchain(?:\/[^'"]*)?['"]/, reason: 'langchain-import' },
  { pattern: /from\s+['"]@langchain\/langgraph(?:\/[^'"]*)?['"]/, reason: 'langgraph-import' },
  { pattern: /from\s+['"]langgraph(?:\/[^'"]*)?['"]/, reason: 'langgraph-import' },
  { pattern: /from\s+['"]openai(?:\/[^'"]*)?['"]/, reason: 'openai-sdk-import' },
  { pattern: /from\s+['"]@anthropic-ai\/sdk(?:\/[^'"]*)?['"]/, reason: 'anthropic-sdk-import' },
  { pattern: /from\s+['"]anthropic(?:\/[^'"]*)?['"]/, reason: 'anthropic-import' },
  { pattern: /from\s+['"]@ai-sdk\/[^'"]+['"]/, reason: 'ai-sdk-import' },
  { pattern: /from\s+['"]ai['"]/, reason: 'vercel-ai-import' },
  { pattern: /from\s+['"]@mastra\/[^'"]+['"]/, reason: 'mastra-import' },
  { pattern: /from\s+['"]crewai(?:\/[^'"]*)?['"]/, reason: 'crewai-import' },
  { pattern: /from\s+['"]@openai\/agents(?:\/[^'"]*)?['"]/, reason: 'openai-agents-sdk' },
  { pattern: /from\s+['"]autogen(?:_agentchat)?(?:\/[^'"]*)?['"]/, reason: 'autogen-import' },
];

// Path segments that indicate non-production files — penalize confidence
const LOW_CONFIDENCE_PATH_SEGMENTS = ['scripts/', 'tools/', 'config/', '__tests__/', 'tests/', 'fixtures/', '__mocks__/'];

/**
 * Check whether a file's relative path contains any low-confidence path segment.
 */
function isLowConfidencePath(relativePath: string): boolean {
  const normalized = relativePath.replace(/\\/g, '/');
  return LOW_CONFIDENCE_PATH_SEGMENTS.some(seg => normalized.includes(seg));
}

/**
 * Check whether a file imports from any known AI/agent framework.
 * Returns the reason string if found, otherwise null.
 */
function detectAIFrameworkImport(content: string): string | null {
  for (const { pattern, reason } of AI_FRAMEWORK_IMPORT_PATTERNS) {
    if (pattern.test(content)) return reason;
  }
  return null;
}

// High-confidence: framework agent constructors / loops
const AGENT_HIGH_PATTERNS: { pattern: RegExp; reason: string }[] = [
  { pattern: /\bAgentExecutor\b/, reason: 'langchain-agent-executor' },
  { pattern: /\bcreate(?:React|OpenAIFunctions|Tool(?:Calling)?|Structured(?:Chat)?)Agent\b/, reason: 'langchain-create-agent' },
  { pattern: /\bnew\s+Agent\s*\(/, reason: 'agent-constructor' },
  { pattern: /from\s+['"]openai\/agents['"]/, reason: 'openai-agents-sdk' },
  { pattern: /from\s+['"]@openai\/agents['"]/, reason: 'openai-agents-sdk' },
  { pattern: /from\s+['"]crewai['"]/, reason: 'crewai-import' },
  { pattern: /from\s+['"]autogen(?:_agentchat)?['"]/, reason: 'autogen-import' },
  { pattern: /from\s+['"]@?langgraph['"]/, reason: 'langgraph-import' },
  { pattern: /from\s+['"]@langchain\/langgraph['"]/, reason: 'langgraph-import' },
  { pattern: /\bStateGraph\s*\(/, reason: 'langgraph-stategraph' },
];

// Medium-confidence: tool calling signals
const AGENT_MEDIUM_PATTERNS: { pattern: RegExp; reason: string }[] = [
  { pattern: /\btool_calls\b/, reason: 'tool-calls' },
  { pattern: /\btoolCalls\b/, reason: 'tool-calls' },
  { pattern: /^\s*tools\s*[:=]\s*\[/, reason: 'tools-array' },
  { pattern: /@tool\b/, reason: 'tool-decorator' },
  { pattern: /\bfunction_call\b/, reason: 'function-call' },
  { pattern: /\btool_choice\b/, reason: 'tool-choice' },
];

// =============================================================================
// Prompt Variable Inference (single vs double brace, per content)
// =============================================================================

export type TemplateDelimiter = 'single' | 'double';

export interface InferredPromptVariables {
  variables: string[];
  delimiter: TemplateDelimiter;
}

/**
 * Detect template variables in prompt-like content, inferring whether the
 * file/prompt uses single-brace `{var}` (MutagenT platform canonical,
 * LangChain PromptTemplate) or double-brace `{{var}}` (Handlebars, Liquid,
 * Mustache, Jinja2) per-content via majority count.
 *
 * Edge cases handled:
 *  - Fenced markdown code blocks (```...```) are stripped before counting so
 *    that embedded JSON examples inside docs do not leak.
 *  - Literal JSON like `{"status": "ok"}` is NOT matched — the single-brace
 *    regex excludes `{` immediately followed by `"`.
 *  - Escaped braces `\{foo\}` are skipped via the `(?<![\\{])` lookbehind.
 *  - `{{foo}}` tokens cannot also match as single-brace (negative lookbehind
 *    + lookahead around the single pattern).
 *
 * Tie-breaking: if singleHits === doubleHits (including the 0/0 case),
 * returns `single` — MutagenT platform canonical.
 */
export function inferPromptVariables(content: string): InferredPromptVariables {
  // Strip fenced code blocks first to avoid false positives from embedded
  // JSON examples inside prompt documentation.
  const stripped = content.replace(FENCED_CODE_PATTERN, '');

  const doubleMatches: string[] = [];
  const singleMatches: string[] = [];

  // Run both regexes with /g so we get every match. Important: create new
  // regex instances to avoid shared lastIndex state bugs across calls.
  const dbl = new RegExp(DOUBLE_VAR_PATTERN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = dbl.exec(stripped)) !== null) {
    if (m[1]) doubleMatches.push(m[1]);
  }

  const sgl = new RegExp(SINGLE_VAR_PATTERN.source, 'g');
  while ((m = sgl.exec(stripped)) !== null) {
    if (m[1]) singleMatches.push(m[1]);
  }

  const delimiter: TemplateDelimiter =
    doubleMatches.length > singleMatches.length ? 'double' : 'single';

  const chosen = delimiter === 'double' ? doubleMatches : singleMatches;

  // Dedupe preserving insertion order.
  const seen = new Set<string>();
  const variables: string[] = [];
  for (const v of chosen) {
    if (!seen.has(v)) {
      seen.add(v);
      variables.push(v);
    }
  }

  return { variables, delimiter };
}

// =============================================================================
// File Walking
// =============================================================================

function walkDir(
  dir: string,
  extensions: string[],
  excludeDirs: string[],
  maxDepth: number,
  currentDepth = 0,
): string[] {
  if (currentDepth >= maxDepth) return [];

  const files: string[] = [];

  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return files;
  }

  for (const entry of entries) {
    const fullPath = join(dir, entry);

    let stat;
    try {
      stat = statSync(fullPath);
    } catch {
      continue;
    }

    if (stat.isDirectory()) {
      // Always ignore .claude — never scan user's claude config/skills folders
      if (entry === '.claude') continue;
      if (excludeDirs.includes(entry)) continue;
      files.push(...walkDir(fullPath, extensions, excludeDirs, maxDepth, currentDepth + 1));
    } else if (stat.isFile()) {
      const ext = extname(entry).toLowerCase();
      if (extensions.includes(ext)) {
        files.push(fullPath);
      }
    }
  }

  return files;
}

// =============================================================================
// Scanners
// =============================================================================

function scanForPrompts(filePath: string, relativePath: string): DiscoveredPrompt[] {
  const results: DiscoveredPrompt[] = [];

  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch {
    return results;
  }

  // Compute the delimiter ONCE over the whole file content — majority wins
  // per FILE, not per line. A line-by-line inference would tie-break to
  // single for every single-var line even in a Handlebars-majority file.
  // See concepts/prompt-variables.md for the full inference contract.
  const fileDelimiter = inferPromptVariables(content).delimiter;

  // AI framework import detected — elevates template-variable hits to 'high'
  const aiFrameworkReason = detectAIFrameworkImport(content);
  const pathPenalty = isLowConfidencePath(relativePath);

  const lines = content.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;

    // Check for template variables — delimiter-aware (single {foo} or double {{foo}}),
    // with JSON-escape + fenced-code safety handled by inferPromptVariables().
    // Per-line inference is used ONLY as a gate to decide whether this line
    // looks template-y; the delimiter attached to every hit is the file-level
    // one computed above.
    const inferred = inferPromptVariables(line);
    if (inferred.variables.length > 0) {
      const preview = line.trim().substring(0, 80);
      // Files importing AI frameworks get 'high'; path-penalized files get 'low'
      const confidence: DiscoveredPrompt['confidence'] = pathPenalty
        ? 'low'
        : aiFrameworkReason
          ? 'high'
          : 'high';
      results.push({
        file: relativePath,
        line: i + 1,
        preview,
        reason: aiFrameworkReason ? `template-variable+${aiFrameworkReason}` : 'template-variable',
        confidence,
        delimiter: fileDelimiter,
      });
    }

    // Check for prompt-like constant declarations
    if (PROMPT_NAME_PATTERN.test(line)) {
      const preview = line.trim().substring(0, 80);
      // Avoid duplicate if same line matched template var
      if (!results.some(r => r.file === relativePath && r.line === i + 1)) {
        const confidence: DiscoveredPrompt['confidence'] = pathPenalty ? 'low' : 'medium';
        results.push({
          file: relativePath,
          line: i + 1,
          preview,
          reason: 'prompt-constant',
          confidence,
          delimiter: fileDelimiter,
        });
      }
    }

    // Check for schema definitions (Zod — only prompt/input/output related)
    if (ZOD_PROMPT_SCHEMA_PATTERN.test(line)) {
      const preview = line.trim().substring(0, 80);
      if (!results.some(r => r.file === relativePath && r.line === i + 1)) {
        results.push({
          file: relativePath,
          line: i + 1,
          preview,
          reason: 'zod-schema',
          confidence: 'low',
          delimiter: fileDelimiter,
        });
      }
    }

    // Check for Pydantic models
    if (PYDANTIC_PATTERN.test(line)) {
      const preview = line.trim().substring(0, 80);
      if (!results.some(r => r.file === relativePath && r.line === i + 1)) {
        const confidence: DiscoveredPrompt['confidence'] = pathPenalty ? 'low' : 'medium';
        results.push({
          file: relativePath,
          line: i + 1,
          preview,
          reason: 'pydantic-model',
          confidence,
          delimiter: fileDelimiter,
        });
      }
    }
  }

  return results;
}

function scanForAgents(filePath: string, relativePath: string): DiscoveredAgent[] {
  const results: DiscoveredAgent[] = [];

  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch {
    return results;
  }

  const pathPenalty = isLowConfidencePath(relativePath);
  // File-level AI framework import check — if found, even medium patterns become high
  const aiFrameworkReason = detectAIFrameworkImport(content);

  const lines = content.split('\n');
  const seen = new Set<number>();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;

    for (const { pattern, reason } of AGENT_HIGH_PATTERNS) {
      if (pattern.test(line) && !seen.has(i)) {
        results.push({
          file: relativePath,
          line: i + 1,
          preview: line.trim().substring(0, 80),
          reason,
          confidence: pathPenalty ? 'low' : 'high',
        });
        seen.add(i);
        break;
      }
    }

    if (seen.has(i)) continue;

    for (const { pattern, reason } of AGENT_MEDIUM_PATTERNS) {
      if (pattern.test(line)) {
        results.push({
          file: relativePath,
          line: i + 1,
          preview: line.trim().substring(0, 80),
          reason,
          // Elevate to 'high' when a known AI framework is imported in this file
          confidence: pathPenalty ? 'low' : (aiFrameworkReason ? 'high' : 'medium'),
        });
        seen.add(i);
        break;
      }
    }
  }

  return results;
}

function scanForMarkers(filePath: string, relativePath: string): MarkerEntry[] {
  const results: MarkerEntry[] = [];

  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch {
    return results;
  }

  const lines = content.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;

    const match = MARKER_START_PATTERN.exec(line);
    if (match) {
      const rawType = match[1]?.toLowerCase();
      const platformId = match[2];
      const validTypes = new Set(['prompt', 'dataset', 'evaluation']);
      if (rawType && validTypes.has(rawType)) {
        results.push({
          file: relativePath,
          line: i + 1,
          type: rawType as MarkerEntry['type'],
          platformId,
        });
      }
    }
  }

  return results;
}

function scanJsonForSchemas(filePath: string, relativePath: string): DiscoveredPrompt[] {
  const results: DiscoveredPrompt[] = [];
  const ext = extname(filePath).toLowerCase();
  if (ext !== '.json') return results;

  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch {
    return results;
  }

  // Quick check before parsing
  if (!SCHEMA_PATTERN.test(content)) return results;

  // JSON schema files don't have `{variable}` templates — inference is
  // meaningless, default to the platform canonical delimiter.
  const preview = `JSON with schema definitions: ${basename(filePath)}`;
  results.push({
    file: relativePath,
    line: 1,
    preview: preview.substring(0, 80),
    reason: 'json-schema',
    confidence: 'medium',
    delimiter: 'single',
  });

  return results;
}

function scanForDatasets(dir: string, rootPath: string, excludeDirs: string[], maxDepth: number): DiscoveredDataset[] {
  const results: DiscoveredDataset[] = [];
  const dataExtensions = ['.json', '.jsonl', '.csv'];
  const files = walkDir(dir, dataExtensions, excludeDirs, maxDepth);

  for (const filePath of files) {
    const relativePath = relative(rootPath, filePath);
    const ext = extname(filePath).toLowerCase();
    const name = basename(filePath, ext);

    let content: string;
    try {
      content = readFileSync(filePath, 'utf-8');
    } catch {
      continue;
    }

    if (ext === '.csv') {
      const lines = content.trim().split('\n');
      if (lines.length > 1) {
        // Check header for dataset-like columns
        const header = lines[0]?.toLowerCase() ?? '';
        if (header.includes('input') || header.includes('output') || header.includes('expected')) {
          results.push({ file: relativePath, name, items: lines.length - 1 });
        }
      }
    } else if (ext === '.jsonl') {
      const lines = content.trim().split('\n').filter(l => l.trim().length > 0);
      if (lines.length > 0) {
        try {
          const first = JSON.parse(lines[0] ?? '{}') as Record<string, unknown>;
          if ('input' in first || 'expectedOutput' in first) {
            results.push({ file: relativePath, name, items: lines.length });
          }
        } catch {
          // Not valid JSONL
        }
      }
    } else if (ext === '.json') {
      try {
        const parsed: unknown = JSON.parse(content);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = parsed[0] as Record<string, unknown>;
          if ('input' in first || 'expectedOutput' in first) {
            results.push({ file: relativePath, name, items: parsed.length });
          }
        }
      } catch {
        // Not valid JSON
      }
    }
  }

  return results;
}

// =============================================================================
// Main Explore Function
// =============================================================================

export function exploreCodebase(options: ExploreOptions): ExploreResult {
  const rootPath = options.path;
  const prompts: DiscoveredPrompt[] = [];
  const datasets: DiscoveredDataset[] = [];
  const markers: MarkerEntry[] = [];
  const agents: DiscoveredAgent[] = [];

  // Walk source files
  const sourceFiles = walkDir(rootPath, options.extensions, options.excludeDirs, options.depth);

  for (const filePath of sourceFiles) {
    const relativePath = relative(rootPath, filePath);

    // Always scan for markers
    markers.push(...scanForMarkers(filePath, relativePath));

    // Heuristic scanning (skip if markers-only mode)
    if (!options.markersOnly) {
      prompts.push(...scanForPrompts(filePath, relativePath));
      agents.push(...scanForAgents(filePath, relativePath));
    }
  }

  // Also scan JSON files for schema patterns (heuristic mode only)
  if (!options.markersOnly) {
    const jsonFiles = walkDir(rootPath, ['.json'], options.excludeDirs, options.depth);
    for (const filePath of jsonFiles) {
      const relativePath = relative(rootPath, filePath);
      prompts.push(...scanJsonForSchemas(filePath, relativePath));
    }

    // Scan for dataset files
    datasets.push(...scanForDatasets(rootPath, rootPath, options.excludeDirs, options.depth));
  }

  return { prompts, datasets, markers, agents };
}

// =============================================================================
// Helpers
// =============================================================================

export function parseExtensions(includeGlob: string): string[] {
  // Parse patterns like "**/*.{ts,js,py,tsx,jsx}" into extensions
  const braceMatch = /\{([^}]+)\}/.exec(includeGlob);
  if (braceMatch?.[1]) {
    return braceMatch[1].split(',').map(ext => `.${ext.trim()}`);
  }

  // Single extension like "**/*.ts"
  const extMatch = /\*\.(\w+)$/.exec(includeGlob);
  if (extMatch?.[1]) {
    return [`.${extMatch[1]}`];
  }

  // Default fallback
  return ['.ts', '.js', '.py', '.tsx', '.jsx'];
}

export function parseExcludeDirs(excludePattern: string): string[] {
  return excludePattern.split(',').map(d => d.trim()).filter(Boolean);
}
